import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Users, Filter, Download, Phone, Mail, MapPin, Clock, ChevronRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SponsorLeads = () => {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchLeads();
  }, [token, statusFilter, scoreFilter]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      let url = `${API}/sponsors/leads?`;
      if (statusFilter && statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (scoreFilter && scoreFilter !== 'all') url += `score=${scoreFilter}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeads(response.data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/sponsors/leads/${leadId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead status updated');
      fetchLeads();
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const exportLeads = () => {
    const csv = [
      ['Name', 'Email', 'Phone', 'Location', 'Score', 'Status', 'Date'].join(','),
      ...leads.map(lead => [
        lead.user?.name || '',
        lead.user?.email || '',
        lead.user?.phone || '',
        `${lead.profile?.city || ''} ${lead.profile?.province_state || ''}`,
        lead.score,
        lead.status,
        new Date(lead.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `spinser-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Leads exported successfully');
  };

  const filteredLeads = leads.filter(lead => 
    lead.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getScoreBadge = (score) => {
    const styles = {
      hot: 'bg-red-100 text-red-700',
      warm: 'bg-amber-100 text-amber-700',
      cold: 'bg-blue-100 text-blue-700'
    };
    return styles[score] || 'bg-neutral-100 text-neutral-700';
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-700',
      contacted: 'bg-amber-100 text-amber-700',
      converted: 'bg-green-100 text-green-700',
      lost: 'bg-neutral-100 text-neutral-700'
    };
    return styles[status] || 'bg-neutral-100 text-neutral-700';
  };

  if (loading && leads.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-12 h-12 border-4 border-[#FF3B30]/20 border-t-[#FF3B30] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sponsor-leads">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit' }}>Leads</h1>
          <p className="text-neutral-600 mt-1">{leads.length} leads from your sponsored giveaways</p>
        </div>
        <Button onClick={exportLeads} variant="outline" className="rounded-xl" data-testid="export-leads-btn">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="search-leads"
          />
        </div>
        <Select value={scoreFilter} onValueChange={setScoreFilter}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="score-filter">
            <SelectValue placeholder="All Scores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scores</SelectItem>
            <SelectItem value="hot">Hot</SelectItem>
            <SelectItem value="warm">Warm</SelectItem>
            <SelectItem value="cold">Cold</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 rounded-xl" data-testid="status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="converted">Converted</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 border border-neutral-200 text-center">
          <Users className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-neutral-900 mb-2">No Leads Yet</h3>
          <p className="text-neutral-500">Sponsor giveaways to start receiving leads</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLeads.map((lead, index) => (
            <motion.div
              key={lead.lead_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-white rounded-2xl p-5 border border-neutral-200 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                {/* User Info */}
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center text-lg font-semibold text-neutral-600">
                    {lead.user?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900">{lead.user?.name || 'Unknown'}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-neutral-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {lead.user?.email}
                      </span>
                      {lead.user?.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          {lead.user.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Location & Timeline */}
                <div className="flex flex-wrap gap-4 text-sm">
                  {lead.profile && (
                    <>
                      <div className="flex items-center gap-1 text-neutral-500">
                        <MapPin className="w-4 h-4" />
                        {lead.profile.city}, {lead.profile.province_state}
                      </div>
                      <span className="text-neutral-400">|</span>
                      <span className="text-neutral-600 capitalize">
                        {lead.profile.timeline?.replace(/_/g, ' ') || 'Unknown'}
                      </span>
                    </>
                  )}
                </div>

                {/* Badges */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getScoreBadge(lead.score)}`}>
                    {lead.score}
                  </span>
                  <span
                    data-testid={`lead-consent-badge-${index}`}
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${lead.sponsor_contact_consent ? 'bg-green-100 text-green-700' : 'bg-neutral-100 text-neutral-500'}`}
                  >
                    {lead.sponsor_contact_consent ? '✓ Consented to Contact' : 'No Contact Consent'}
                  </span>
                  {lead.territory_match !== null && lead.territory_match !== undefined && (
                    <span
                      data-testid={`lead-territory-badge-${index}`}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${lead.territory_match ? 'bg-[#FF3B30]/10 text-[#FF3B30]' : 'bg-neutral-100 text-neutral-500'}`}
                    >
                      {lead.territory_match ? '📍 In Your Territory' : 'Outside Territory'}
                    </span>
                  )}
                  <Select value={lead.status} onValueChange={(v) => updateLeadStatus(lead.lead_id, v)}>
                    <SelectTrigger className={`w-32 h-8 text-xs font-semibold ${getStatusBadge(lead.status)}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="converted">Converted</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date */}
                <div className="flex items-center gap-1 text-sm text-neutral-400">
                  <Clock className="w-4 h-4" />
                  {new Date(lead.created_at).toLocaleDateString()}
                </div>
              </div>

              {/* Questionnaire Insights */}
              {lead.questionnaire && (
                <div className="mt-4 pt-4 border-t border-neutral-100 space-y-2" data-testid={`lead-questionnaire-${index}`}>
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {lead.questionnaire.selling_intent && lead.questionnaire.selling_intent !== 'No' && (
                      <span className={`px-2.5 py-1 rounded-full font-semibold ${lead.questionnaire.selling_intent.includes('3 months') || lead.questionnaire.selling_intent.includes('6 months') ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        🏠 Selling: {lead.questionnaire.selling_intent.replace('Yes, ', '')}
                      </span>
                    )}
                    {lead.questionnaire.buying_intent && lead.questionnaire.buying_intent !== 'No' && (
                      <span className="px-2.5 py-1 rounded-full font-semibold bg-blue-100 text-blue-700">
                        🔑 Buying: {lead.questionnaire.buying_intent.replace('Yes, ', '')}
                      </span>
                    )}
                    {lead.questionnaire.project_timeline && (
                      <span className="px-2.5 py-1 rounded-full font-medium bg-neutral-100 text-neutral-600">
                        ⏱ Project: {lead.questionnaire.project_timeline}
                      </span>
                    )}
                    {lead.questionnaire.contact_preference && (
                      <span className="px-2.5 py-1 rounded-full font-medium bg-neutral-100 text-neutral-600">
                        Prefers: {lead.questionnaire.contact_preference}
                      </span>
                    )}
                  </div>
                  {lead.questionnaire.improvement_interests?.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-medium text-neutral-500">Interests:</span>
                      {lead.questionnaire.improvement_interests.map((interest) => (
                        <span key={interest} className="px-2 py-0.5 rounded-full text-xs bg-[#FF3B30]/5 text-[#FF3B30] border border-[#FF3B30]/20">
                          {interest}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Score Reasons */}
              {lead.score_reasons?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-neutral-100">
                  <p className="text-sm text-neutral-500">
                    <span className="font-medium">AI Insights:</span> {lead.score_reasons.join(' • ')}
                  </p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SponsorLeads;
